import { useTheme } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { RefObject, useEffect, useMemo, useRef } from 'react';
import Chart from 'react-apexcharts';

import { formatNumber } from '../../utils/formatting';

interface StackedBarChartProps {
  series: ApexAxisChartSeries;
  categories?: string[];
  onClick?: (event, chartContext, config) => void;
  id?: string;
  group?: string;
  fileName?: string;
  isLoading?: boolean;
  customOptions?: (defaultOptions: ApexOptions) => ApexOptions;
  height?: number | string;
  width?: number | string;
  ref?: RefObject<any>;
}

/**
 * Renders data into a stacked bar chart over time.
 */

const StackedBarChart: React.FC<StackedBarChartProps> = ({
  series,
  categories,
  onClick,
  id,
  group,
  fileName,
  height = 350,
  width = '100%',
  isLoading,
  customOptions,
  ref,
}) => {
  /* Styling */
  const chartRef = useRef(null);

  const theme = useTheme();
  const h3 = theme.typography['h3'];
  const subtitle1 = theme.typography['subtitle1'];

  const barWidth = 30 + 60 / (1 + 30 * Math.exp(-series.length / 3));

  const defaultOptions: ApexOptions = {
    chart: {
      id,
      group,
      stacked: false,
      selection: {
        enabled: false,
      },
      zoom: {
        enabled: false,
      },
      events: {
        click: onClick,
      },
      toolbar: {
        autoSelected: 'selection',
        export: {
          svg: {
            filename: fileName,
          },
          png: {
            filename: fileName,
          },
          csv: {
            filename: fileName,
            headerCategory: 'Timestamp',
            categoryFormatter: (value: any) => {
              return dayjs(value).format('DD-MM-YYYY HH:mm:ss');
            },
          },
        },
      },
    },
    title: {
      style: {
        fontFamily: theme.typography.fontFamily,
        fontSize: h3.fontSize.toString() ?? '18',
        fontWeight: h3.fontWeight,
      },
    },
    subtitle: {
      style: {
        fontFamily: subtitle1.fontFamily,
        fontSize: subtitle1.fontSize.toString() ?? '15',
        fontWeight: subtitle1.fontWeight,
      },
    },
    legend: {
      position: 'top',
      fontFamily: theme.typography.fontFamily,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: barWidth + '%',
      },
    },
    noData: {
      text: isLoading
        ? '// Data load in progress...'
        : '// Load complete: Dataset empty',
      align: 'center',
      verticalAlign: 'middle',
      offsetX: 0,
      offsetY: 0,
      style: {
        color: undefined,
        fontFamily: theme.typography.fontFamily,
        fontSize: h3.fontSize?.toString() ?? '18',
      },
    },
    xaxis: {
      type: 'datetime',
      categories: categories,
      labels: {
        style: {
          colors: theme.palette.text.secondary,
          fontFamily: theme.typography.fontFamily,
        },
        datetimeUTC: false,
        datetimeFormatter: {
          day: 'dd/MM',
        },
      },
      title: {
        text: '',
        offsetY: -25,
      },
      tooltip: {
        enabled: false,
        formatter: (value) => dayjs(value).format('D MMM YYYY, HH:mm'),
      },
    },
    yaxis: {
      tickAmount: 3,
      labels: {
        style: {
          colors: theme.palette.text.secondary,
          fontFamily: theme.typography.fontFamily,
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    grid: {
      borderColor: theme.palette.divider,
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: theme.palette.mode,
      style: {
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.toString(),
      },
      y: {
        formatter: function (value) {
          return `${formatNumber(value)}`;
        },
      },
    },
    stroke: {
      width: 0,
    },
  };

  const options = useMemo(() => {
    const ops = {
      ...defaultOptions,
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: 30 + 60 / (1 + 30 * Math.exp(-series.length / 3)) + '%',
        },
      },
    };
    return customOptions != null ? customOptions(ops) : ops;
  }, [series, customOptions]);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.chart.updateSeries(series);
    }
  }, [series]);

  return (
    <Chart
      type='bar'
      options={options}
      series={series}
      height={height}
      width={width}
    />
  );
};

export default StackedBarChart;
